/**
 * GroupOverviewTab: the complete group-level financial snapshot.
 *
 * Layout (dashboards round 3, authoritative, top to bottom): stat cards (2
 * rows of 4) -- Gross Revenue chart with By Jurisdiction beneath it -- Cash
 * Runway | FX Exposure (the only place Cash Runway appears on this
 * dashboard) -- Bank Balance (full width) -- Net Assets (full width) --
 * Monthly Cashflow (full width). Nothing else: the Attention badge, Coverage
 * Caveat Ribbon, Unattributed Warning, and the footer info line (actuals
 * cutoff / stale threshold / FX rate) were all deleted this round -- their
 * endpoints/fields remain live for other consumers (e.g. data.coverage,
 * data.unattributed, data.actuals_cutoff), just no longer rendered here. By
 * Scope, Intercompany Eliminations, and Monthly Trend 2026 were removed in
 * an earlier redesign step and stay removed. Stat cards (step 2) compose
 * PP/BR OS/A&L/bank-balance canonical figures via lib/groupStatCards.js; see
 * that file for the source of each number.
 *
 * Uses function Foo(props) with body destructuring per 147f605 guard.
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Loader2, RefreshCw, AlertTriangle, TrendingUp,
  Wallet, Clock, Globe, PieChart, Building2, Home, Download,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../../ui/popover';
import { CurrencyToggle } from '../../CurrencyToggle';
import {
  groupDashboardAPI, dashboardAPI, portfolioAPI, reportsAPI,
  invoiceFinancingAPI, assetsLiabilitiesAPI, bankAccountsAPI, forecastAPI,
  bankBalanceHistoryAPI,
} from '../../../lib/api';
import { usePortfolioPrefsStore } from '../../../store';
import { convertAmount } from '../../../lib/currencyConvert';
import { formatCompact, formatCurrencyFull, formatPercent } from '../../../lib/chartFormatters';
import { computeDerivedMetrics, convertDerivedToDisplayCurrency, wholeMonthBasis } from '../../../lib/portfolioDerived';
import { computeGroupStatCards } from '../../../lib/groupStatCards';
import { computeNetAssetsCard } from '../../../lib/entityStatCards';
import { ForecastTotalsFooter } from '../ForecastTotalsFooter';
import {
  GROSS_REVENUE_SEGMENTS,
  buildGrossRevenueChartData,
  buildGroupMarginChartData,
  computeGrossRevenueYtd,
  normalizeMonthlyByRegionToGbpKeys,
} from '../../../lib/groupGrossRevenueChart';
import { MarginChart } from '../MarginChart';
import { NetRevenueChart } from '../NetRevenueChart';
import { buildNetRevenueChartData } from '../../../lib/groupNetRevenueChart';
import {
  buildCashDevelopmentChartData,
  buildJurisdictionCashForecastRows,
} from '../../../lib/cashDevelopmentChart';
import { CashRunwayCard } from '../CashRunwayCard';
import { GroupJurisdictionTab } from './GroupJurisdictionTab';
import { buildMonthlyCashflowChartData, computeAvgNetCashflow } from '../../../lib/monthlyCashflowChart';
import { MonthlyCashflowChart } from '../MonthlyCashflowChart';
import { buildNetAssetsChartData, sumNetAssetsSeries } from '../../../lib/netAssetsChart';
import { NetAssetsChart } from '../NetAssetsChart';
import { buildBankBalanceHistoryChartData } from '../../../lib/entityBankBalanceHistoryChart';
import { useJobPolling } from '../../admin/JobPollingPanel';
import { BankBalanceHistoryChart } from '../BankBalanceHistoryChart';
import { ForecastCaveat, forecastTotalLabelContent } from '../../../lib/forecastChartUtils';
import { getErrorMessage } from '../../../lib/errors';
import {
  PDF_MARGIN, drawBrandedHeader, drawBrandedFooter, TABLE_DEFAULTS,
  pdfCurrencyFormatter, addFormattedSheet, EXCEL_CURRENCY_FORMAT,
} from '../../../lib/exportUtils';
import { toast } from 'sonner';
import {
  ResponsiveContainer, ComposedChart, Bar, XAxis, YAxis,
  CartesianGrid, Tooltip, LabelList, Legend, Text,
} from 'recharts';

const SHORT_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// ── Export (step 6): stat cards + both charts' underlying monthly tables ──
// Shared row-builders so the PDF and Excel exports render identical figures.

function buildStatCardRows(cards, fmt) {
  return [
    ['Booking Volume', fmt(cards.grossRevenue.headline), `Property Portfolio ${fmt(cards.grossRevenue.pp)}; BR OS ${fmt(cards.grossRevenue.brosOs)}`],
    ['Net Revenue', fmt(cards.netRevenue.headline), `Property Portfolio ${fmt(cards.netRevenue.pp)}; BR OS ${fmt(cards.netRevenue.brosOs)}`],
    ['Gross Margin', `${fmt(cards.grossMargin.headline)} (${formatPercent(cards.grossMargin.pct, 1)})`, `Property Portfolio ${fmt(cards.grossMargin.pp)}; BR OS ${fmt(cards.grossMargin.brosOs)}`],
    ['Net Margin', `${fmt(cards.netMargin.headline)} (${formatPercent(cards.netMargin.pct, 1)})`, `Property Portfolio ${fmt(cards.netMargin.pp)}; BR OS ${fmt(cards.netMargin.brosOs)}`],
    ['Bank Balance', fmt(cards.bankBalance.headline), `UK ${fmt(cards.bankBalance.uk)}; France ${fmt(cards.bankBalance.france)}; Personal ${fmt(cards.bankBalance.personal)}; Unassigned ${fmt(cards.bankBalance.unassigned)}`],
    ['Net Assets', cards.netAssetsCard.error ? 'No data' : fmt(cards.netAssetsCard.headline), ''],
    ['Properties', String(cards.propertiesCard.headline), ''],
    ['Avg Net Cashflow (YTD, complete months)', fmt(cards.avgMonthlyNetCashflowCard.headline), ''],
  ];
}

function buildGrossRevenueRows(rows, fmt) {
  return rows.map(function (r) {
    return [
      r.month, fmt(r['BR OS']), fmt(r.Portfolio), r.Portfolio_forecast != null ? fmt(r.Portfolio_forecast) : '',
      fmt(r._grossMargin), fmt(r._netMargin),
    ];
  });
}

// dashboards round 3: buildCashDevelopmentChartData dropped its forecast
// segment (this chart has no forecast of its own any more, see
// lib/cashDevelopmentChart.js), so every row is 'actual' -- a month with no
// snapshot/reconstruction coverage renders with blank cells (fmt(null) ===
// ''), never a fabricated zero.
function buildCashDevelopmentRows(chartData, fmt) {
  return (chartData.actual || []).map(function (r) {
    return [r.month, fmt(r.UK), fmt(r.France), fmt(r.Personal), fmt(r._total), 'actual'];
  });
}

async function exportOverallPDF({ cards, grossRevenueRows, cashDevChartData, displayCurrency, year }) {
  const { default: jsPDF } = await import('jspdf');
  const { default: autoTable } = await import('jspdf-autotable');

  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const fmt = pdfCurrencyFormatter(displayCurrency);
  let y = drawBrandedHeader(doc, {
    title: 'Group Dashboard',
    scopeLine: `Multi-entity consolidated view, ${year}`,
  });

  autoTable(doc, {
    ...TABLE_DEFAULTS,
    startY: y,
    head: [['Card', 'Value', 'Breakdown']],
    body: buildStatCardRows(cards, fmt),
  });

  y = doc.lastAutoTable.finalY + 10;
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text('Booking Volume (monthly)', PDF_MARGIN, y);
  doc.setFont('helvetica', 'normal');
  autoTable(doc, {
    ...TABLE_DEFAULTS,
    startY: y + 4,
    head: [['Month', 'BR OS', 'Portfolio', 'Portfolio (forecast)', 'Gross Margin', 'Net Margin']],
    body: buildGrossRevenueRows(grossRevenueRows, fmt),
  });

  doc.addPage();
  y = drawBrandedHeader(doc, { title: 'Group Dashboard', scopeLine: 'Bank Balance' });
  autoTable(doc, {
    ...TABLE_DEFAULTS,
    startY: y,
    head: [['Month', 'UK', 'France', 'Personal', 'Total', 'Source']],
    body: buildCashDevelopmentRows(cashDevChartData, fmt),
  });

  drawBrandedFooter(doc, { footerNote: 'Bonjour Residences — Group Dashboard export' });
  doc.save(`overall-dashboard-${year}.pdf`);
}

async function exportOverallExcel({ cards, grossRevenueRows, cashDevChartData, displayCurrency, year }) {
  const XLSX = await import('xlsx');
  const wb = XLSX.utils.book_new();
  const fmt = (v) => (v != null ? Math.round(v * 100) / 100 : '');

  addFormattedSheet(wb, XLSX, 'Stat Cards', [
    ['Card', 'Value', 'Breakdown'],
    ...buildStatCardRows(cards, fmt),
  ]);

  addFormattedSheet(wb, XLSX, 'Booking Volume (monthly)', [
    ['Month', 'BR OS', 'Portfolio', 'Portfolio (forecast)', 'Gross Margin', 'Net Margin'],
    ...buildGrossRevenueRows(grossRevenueRows, fmt),
  ], { formats: { 1: EXCEL_CURRENCY_FORMAT, 2: EXCEL_CURRENCY_FORMAT, 3: EXCEL_CURRENCY_FORMAT, 4: EXCEL_CURRENCY_FORMAT, 5: EXCEL_CURRENCY_FORMAT } });

  addFormattedSheet(wb, XLSX, 'Bank Balance', [
    ['Month', 'UK', 'France', 'Personal', 'Total', 'Source'],
    ...buildCashDevelopmentRows(cashDevChartData, fmt),
  ], { formats: { 1: EXCEL_CURRENCY_FORMAT, 2: EXCEL_CURRENCY_FORMAT, 3: EXCEL_CURRENCY_FORMAT, 4: EXCEL_CURRENCY_FORMAT } });

  XLSX.writeFile(wb, `overall-dashboard-${year}${displayCurrency === 'EUR' ? '-eur' : ''}.xlsx`);
}

function relativeTime(props) {
  var isoDate = props;
  if (!isoDate) return 'Never';
  var diff = Date.now() - new Date(isoDate).getTime();
  var hours = diff / 3600000;
  if (hours < 1) return Math.round(hours * 60) + 'm ago';
  if (hours < 24) return Math.round(hours) + 'h ago';
  return Math.round(hours / 24) + 'd ago';
}

// staleConnections (kpis.cash_now.stale_connections): the specific active,
// included connections behind stale_threshold_hours -- named so the badge
// tells the operator WHICH feed is behind instead of a bare "Nd ago" that
// could otherwise be read as every feed being that old. This name must be
// visible without a hover: a bare native `title` attribute was the only
// carrier of this text before, which is undiscoverable (no visual affordance
// it exists), requires ~1-1.5s of dead-still hover to surface, is defeated by
// any mouse jitter, and never fires on touch or via keyboard -- in practice
// indistinguishable from the tooltip "rendering nothing". staleSummaryLabel
// puts the same information inline; the title stays only as a redundant,
// progressive-detail fallback (see FreshnessBadge below).
function staleConnectionsTitle(staleConnections) {
  if (!staleConnections || staleConnections.length === 0) return undefined;
  return 'Stale: ' + staleConnections.map(function (c) {
    return c.entity + ' ' + c.bank + ' (' + relativeTime(c.last_synced) + ')';
  }).join(', ');
}

// staleConnections is sorted oldest-first by the backend (routes/
// group_dashboard.py, sort key last_synced ascending).
function staleSummaryLabel(freshness, staleConnections) {
  if (!staleConnections || staleConnections.length === 0) return relativeTime(freshness);
  if (staleConnections.length === 1) {
    var only = staleConnections[0];
    return (only.bank || only.entity) + ' · ' + relativeTime(only.last_synced);
  }
  return staleConnections.length + ' feeds stale · oldest ' + relativeTime(staleConnections[0].last_synced);
}

export function FreshnessBadge(props) {
  var freshness = props.freshness;
  var stale = props.stale;
  var staleConnections = props.staleConnections;
  var navigate = useNavigate();
  var open_state = useState(false);
  var open = open_state[0];
  var setOpen = open_state[1];

  if (!freshness) {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-amber-600 dark:text-amber-400">
        <AlertTriangle className="h-3 w-3" /> No data
      </span>
    );
  }
  if (stale) {
    var hasConnections = staleConnections && staleConnections.length > 0;
    if (!hasConnections) {
      // No individually-stale active connection to name (freshness itself
      // stale with an empty list shouldn't happen given how the backend
      // derives both from the same active-position set, but degrade to the
      // bare badge rather than open an empty popover).
      return (
        <span className="inline-flex items-center gap-1 text-xs text-amber-600 dark:text-amber-400">
          <AlertTriangle className="h-3 w-3" /> {relativeTime(freshness)}
        </span>
      );
    }
    // Enable Banking is the fix surface for this incident class (reconsent
    // relink, stale-consent). Deep-link straight to that card instead of a
    // bare /integrations landing when any stale feed is EB-sourced.
    var hasEbStale = staleConnections.some(function (c) { return c.source === 'enablebanking'; });
    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            title={staleConnectionsTitle(staleConnections)}
            className="inline-flex items-center gap-1 text-xs text-amber-600 dark:text-amber-400 w-full text-left hover:underline focus:outline-none focus-visible:ring-1 focus-visible:ring-amber-500 rounded"
          >
            <AlertTriangle className="h-3 w-3 flex-shrink-0" />
            <span className="leading-tight">{staleSummaryLabel(freshness, staleConnections)}</span>
          </button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-80 text-xs space-y-3">
          <p className="font-medium text-sm">Stale bank connections</p>
          <div className="space-y-2">
            {staleConnections.map(function (c, i) {
              return (
                <div key={i} className="flex items-baseline justify-between gap-3">
                  <span className="font-medium truncate">{c.entity}{c.bank ? ' · ' + c.bank : ''}</span>
                  <span className="text-muted-foreground flex-shrink-0">{relativeTime(c.last_synced)}</span>
                </div>
              );
            })}
          </div>
          <Button
            type="button"
            variant="link"
            className="h-auto p-0 text-xs"
            onClick={function () {
              setOpen(false);
              navigate(hasEbStale ? '/integrations?scrollTo=enablebanking' : '/integrations');
            }}
          >
            Go to Integrations
          </Button>
        </PopoverContent>
      </Popover>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
      <Clock className="h-3 w-3" /> {relativeTime(freshness)}
    </span>
  );
}

// StatCard: group headline + optional percentage + optional sublines (used
// for the "PP ..." / "BR OS ..." pairs on row 1, the UK/France/Personal
// breakdown on the Bank Balance card, and the single-figure row-2 cards).
function StatCard(props) {
  var label = props.label;
  var value = props.value;
  // Compact card figures (formatCompact) always pair with valueTitle /
  // per-line title -- the full-precision (formatCurrencyFull) string set as
  // the native `title` attribute, so hovering reveals the exact figure
  // without cluttering the card. See lib/chartFormatters.js.
  var valueTitle = props.valueTitle;
  var Icon = props.icon;
  var percentage = props.percentage;
  var lines = props.lines;
  var freshness = props.freshness;
  var stale = props.stale;
  var staleConnections = props.staleConnections;
  var showFreshness = props.showFreshness;
  var className = props.className;
  return (
    <Card className={className}>
      <CardContent className="p-5">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-primary/10">
            <Icon className="h-5 w-5 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm text-muted-foreground">{label}</p>
            <div className="flex items-baseline gap-2 flex-wrap">
              <p className="text-2xl font-bold font-mono tracking-tight" title={valueTitle}>{value}</p>
              {percentage != null && (
                <span className="text-xs font-mono font-medium text-muted-foreground">{percentage}</span>
              )}
            </div>
            {lines && lines.length > 0 && (
              <div className="mt-1 space-y-0.5">
                {lines.map(function (l, i) {
                  return (
                    <p key={i} className="text-xs text-muted-foreground flex justify-between gap-3">
                      <span>{l.label}</span>
                      <span className="font-mono" title={l.title}>{l.value}</span>
                    </p>
                  );
                })}
              </div>
            )}
          </div>
        </div>
        {/* Full card width, not squeezed into the icon row: the multi-feed
            summary ("N feeds stale · oldest NNNd ago") is longer than the
            ~90px this row has left after the icon + label column, and was
            previously clipped mid-number by a max-w-[160px] truncate. */}
        {showFreshness !== false && (
          <div className="mt-2">
            <FreshnessBadge freshness={freshness} stale={stale} staleConnections={staleConnections} />
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// GrossRevenueChart: 12-month stacked bar of GROSS revenue for the BR OS and
// Portfolio scopes ONLY (Companies and Personal are intentionally excluded,
// so this is NOT group income). Rework of the former "Monthly Income: BR OS +
// Portfolio" chart (dashboard redesign step 4): revenue basis switched from
// net/owner-revenue to gross, BR OS/Portfolio stacking order inverted (BR OS
// now bottom, Portfolio top), per-segment value labels added, and Portfolio
// forward-booking forecast overlaid on the 3 months after the actuals
// boundary. Data model lives in lib/groupGrossRevenueChart.js so the
// combined === BR OS + Portfolio invariant (actuals only; forecast never
// merges into the actuals bars or total) is behaviourally tested. Gross/net
// margin now live on the dedicated MarginChart directly below, not here.
function GrossRevenueChart(props) {
  var chartData = props.chartData;
  var displayCurrency = props.displayCurrency;
  var year = props.year;

  var ytd = useMemo(function () {
    return computeGrossRevenueYtd(chartData);
  }, [chartData]);

  if (!chartData || chartData.length === 0) return null;

  var hasForecast = chartData.some(function (d) { return d._isForecast; });
  var lastLabel = GROSS_REVENUE_SEGMENTS[GROSS_REVENUE_SEGMENTS.length - 1].label;
  var segmentLabelFormatter = function (v) { return v > 0 ? formatCompact(v, displayCurrency) : ''; };

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-lg">Booking Volume (monthly) {year}</CardTitle>
        <p className="text-xs text-muted-foreground">
          Gross value of bookings BEFORE OTA/channel commission; for BR OS the
          gross booking volume (client total less VAT), before the supplier
          payment. BR OS and Portfolio scopes only, not group income.
        </p>
        <ForecastCaveat show={hasForecast} />
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={380}>
          <ComposedChart data={chartData} margin={{ top: 24, right: 10, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} opacity={0.3} />
            <XAxis dataKey="month" tick={{ fontSize: 12 }} />
            <YAxis
              tickFormatter={function (v) { return formatCompact(v, displayCurrency); }}
              tick={{ fontSize: 11 }}
              width={60}
            />
            <Tooltip
              formatter={function (val, name) { return [formatCurrencyFull(val, displayCurrency), name]; }}
              contentStyle={{ fontSize: 12 }}
            />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            {/* Actual bars: BR OS (bottom), Portfolio (top) -- inverted from the
                former chart. Each segment carries its own value label; the
                stacked TOTAL rides whichever segment is the topmost non-zero
                one for that month (_carrier), read from chartData by index so
                the label only appears on actual (non-forecast) months. */}
            {GROSS_REVENUE_SEGMENTS.map(function (seg) {
              return (
                <Bar key={seg.key} dataKey={seg.label} stackId="grossRevenue" fill={seg.color}
                  radius={seg.label === lastLabel ? [2, 2, 0, 0] : [0, 0, 0, 0]}>
                  <LabelList dataKey={seg.label} position="inside" fontSize={10}
                    formatter={segmentLabelFormatter} fill="#fff" />
                  <LabelList
                    position="top"
                    content={(lp) => {
                      const { x, y, width, index } = lp;
                      const row = chartData[index];
                      if (!row || row._carrier !== seg.key || !row._total) return null;
                      return (
                        <Text x={x + width / 2} y={y - 7} textAnchor="middle"
                          verticalAnchor="end" fontSize={11} fontWeight={700}
                          fill="hsl(var(--foreground))">
                          {formatCompact(row._total, displayCurrency)}
                        </Text>
                      );
                    }}
                  />
                </Bar>
              );
            })}
            {/* Portfolio forward-booking forecast: separate segment, never
                summed into the actuals bars/total, visually distinct
                (lighter fill, dashed outline), explicitly labelled. */}
            <Bar dataKey="BR OS_forecast" name="BR OS (forecast)" stackId="grossRevenue"
              fill="#3B82F6" fillOpacity={0.35} stroke="#3B82F6" strokeDasharray="4 2" strokeWidth={1}
              radius={[0, 0, 0, 0]}>
              <LabelList dataKey="BR OS_forecast" position="inside" fontSize={10}
                formatter={segmentLabelFormatter} fill="#1E3A8A" />
              {/* Stacked forecast TOTAL, drawn on whichever forecast segment
                  is topmost for that month (_forecastCarrier); a month with
                  only a BR OS forecast carries it here. */}
              <LabelList position="top"
                content={forecastTotalLabelContent(chartData, 'BR OS_forecast',
                  { formatter: function (v) { return formatCompact(v, displayCurrency); }, fill: '#1E3A8A' })} />
            </Bar>
            <Bar dataKey="Portfolio_forecast" name="Portfolio (forecast)" stackId="grossRevenue"
              fill="#10B981" fillOpacity={0.35} stroke="#10B981" strokeDasharray="4 2" strokeWidth={1}
              radius={[2, 2, 0, 0]}>
              {/* Per-segment value moved inside the bar so it no longer
                  competes with the total above it. */}
              <LabelList dataKey="Portfolio_forecast" position="inside" fontSize={10}
                formatter={segmentLabelFormatter} fill="#065F46" />
              <LabelList position="top"
                content={forecastTotalLabelContent(chartData, 'Portfolio_forecast',
                  { formatter: function (v) { return formatCompact(v, displayCurrency); }, fill: '#047857' })} />
            </Bar>
          </ComposedChart>
        </ResponsiveContainer>
        <div className="mt-2 text-sm text-muted-foreground flex items-center gap-4 flex-wrap">
          <span>YTD Booking Volume:</span>
          {GROSS_REVENUE_SEGMENTS.map(function (seg) {
            return (
              <span key={seg.key} className="inline-flex items-center gap-1">
                <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ backgroundColor: seg.color }} />
                <span>{seg.label}</span>
                <span className="font-mono font-semibold text-foreground">
                  {formatCompact(ytd[seg.label], displayCurrency)}
                </span>
              </span>
            );
          })}
          <span className="inline-flex items-center gap-1">
            <span className="inline-block w-2.5 h-2.5 rounded-full bg-foreground" />
            <span>Combined</span>
            <span className="font-mono font-semibold text-foreground">
              {formatCompact(ytd._combined, displayCurrency)}
            </span>
          </span>
        </div>
        <ForecastTotalsFooter
          data={chartData}
          series={GROSS_REVENUE_SEGMENTS.map(function (seg) {
            return { key: seg.label, label: seg.label, color: seg.color };
          })}
          currency={displayCurrency}
          label="Booking Volume"
        />
      </CardContent>
    </Card>
  );
}

function FxExposurePanel(props) {
  var fxExposure = props.fxExposure;
  var displayCurrency = props.displayCurrency;
  if (!fxExposure) return null;
  var data = fxExposure.group;
  if (!data || !data.currencies || data.currencies.length === 0) return null;

  var shareKey = displayCurrency === 'GBP' ? 'share_pct_gbp' : 'share_pct_eur';
  var totalVal = displayCurrency === 'GBP' ? data.total_gbp : data.total_eur;
  var convertedKey = displayCurrency === 'GBP' ? 'gbp' : 'eur';

  var COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#8B5CF6', '#EC4899', '#06B6D4'];

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <Globe className="h-5 w-5" />
            FX Exposure
          </CardTitle>
          <span className="text-sm text-muted-foreground">
            Total: {formatCurrencyFull(totalVal, displayCurrency)}
          </span>
        </div>
      </CardHeader>
      <CardContent>
        <div className="flex h-6 rounded-md overflow-hidden mb-4">
          {data.currencies.map(function (c, i) {
            var pct = c[shareKey] || 0;
            if (pct < 0.5) return null;
            return (
              <div
                key={c.currency}
                style={{ width: pct + '%', backgroundColor: COLORS[i % COLORS.length] }}
                className="flex items-center justify-center text-[10px] text-white font-medium"
                title={c.currency + ': ' + pct + '%'}
              >
                {pct >= 8 ? c.currency + ' ' + pct + '%' : ''}
              </div>
            );
          })}
        </div>
        <div className="space-y-2">
          {data.currencies.map(function (c, i) {
            return (
              <div key={c.currency} className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2">
                  <div
                    className="w-3 h-3 rounded-sm"
                    style={{ backgroundColor: COLORS[i % COLORS.length] }}
                  />
                  <span className="font-medium">{c.currency}</span>
                </div>
                <div className="flex items-center gap-4 font-mono text-xs">
                  <span className="text-muted-foreground">
                    {formatCurrencyFull(c.native, c.currency)}
                  </span>
                  <span>{formatCurrencyFull(c[convertedKey], displayCurrency)}</span>
                  <span className="w-12 text-right">{c[shareKey]}%</span>
                </div>
              </div>
            );
          })}
        </div>
        {fxExposure.unassigned && fxExposure.unassigned.currencies.length > 0 && (
          <div className="mt-3 pt-3 border-t">
            <p className="text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1">
              <AlertTriangle className="h-3 w-3" />
              Unassigned accounts: {fxExposure.unassigned.currencies.map(function (c) {
                return c.currency + ' ' + formatCurrencyFull(c.native, c.currency);
              }).join(', ')}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// BR OS P&L net, year to date: net_result_actual summed up to and including
// the current month (the same predicate as services/entity_pnl.ytd_actuals).
// BR OS lease voids (master-lease months no booking covers), year to date:
// the BR OS fixed cost between Net Revenue and Gross Margin (A148).
export function brosLeaseVoidsYtd(pnl, year) {
  var now = new Date();
  var cur = now.getFullYear() + '-' + String(now.getMonth() + 1).padStart(2, '0');
  return ((pnl || {}).months || []).reduce(function (sum, m) {
    var mk = m.month || '';
    if (mk.indexOf(String(year)) !== 0 || mk > cur) return sum;
    return sum + (parseFloat(m.lease_voids != null ? m.lease_voids : 0) || 0);
  }, 0);
}

export function mergeBrosPnlCosts(months, pnl) {
  var byMonth = {};
  ((pnl || {}).months || []).forEach(function (m) { byMonth[m.month] = m; });
  return (months || []).map(function (m) {
    var p = byMonth[m.month] || {};
    return Object.assign({}, m, {
      lease_voids: parseFloat(p.lease_voids || 0) || 0,
      overheads: parseFloat(p.overheads || 0) || 0,
    });
  });
}

function brosNetActualYtd(pnl, year) {
  var now = new Date();
  var cur = now.getFullYear() + '-' + String(now.getMonth() + 1).padStart(2, '0');
  return ((pnl || {}).months || []).reduce(function (sum, m) {
    var mk = m.month || '';
    if (mk.indexOf(String(year)) !== 0 || mk > cur) return sum;
    return sum + (parseFloat(m.net_result_actual != null ? m.net_result_actual : m.net_result) || 0);
  }, 0);
}

export function GroupOverviewTab() {
  var prefs = usePortfolioPrefsStore();
  var displayCurrency = prefs.displayCurrency;
  var setDisplayCurrency = prefs.setDisplayCurrency;
  var data_state = useState(null);
  var data = data_state[0];
  var setData = data_state[1];
  var runway_state = useState(null);
  var cashRunway = runway_state[0];
  var setCashRunway = runway_state[1];
  var loading_state = useState(true);
  var loading = loading_state[0];
  var setLoading = loading_state[1];
  var refreshing_state = useState(false);
  var refreshing = refreshing_state[0];
  var setRefreshing = refreshing_state[1];
  // Stat-card inputs (step 2): each fetched from its own canonical endpoint,
  // combined by lib/groupStatCards.js. null until loaded.
  var extras_state = useState(null);
  var extras = extras_state[0];
  var setExtras = extras_state[1];
  var exportingPdf_state = useState(false);
  var isExportingPDF = exportingPdf_state[0];
  var setIsExportingPDF = exportingPdf_state[1];
  var exportingExcel_state = useState(false);
  var isExportingExcel = exportingExcel_state[0];
  var setIsExportingExcel = exportingExcel_state[1];
  // Bank Balance chart (per-bank lines + black consolidation): group scope,
  // same scope-parameterized endpoint/component the entity/aggregate
  // dashboards use (bankBalanceHistoryAPI + BankBalanceHistoryChart) --
  // fetched in its own effect, keyed on displayCurrency, because this
  // endpoint bakes display_currency in server-side (unlike
  // reportsAPI.getCashDevelopment below, which returns both currencies
  // pre-computed), same pattern DashboardPage.jsx uses for its own
  // entityBankHistory.
  var groupBankHistory_state = useState(null);
  var groupBankHistory = groupBankHistory_state[0];
  var setGroupBankHistory = groupBankHistory_state[1];

  var fetchData = useCallback(function (showRefreshToast) {
    var year = new Date().getFullYear();
    return Promise.allSettled([
      groupDashboardAPI.get({ year: year }),
      // Group scope: the runway must cover the same cash population as the
      // Cash Now KPI on this screen (all entities + unassigned), not the
      // entities-only default that would silently exclude personal cash.
      dashboardAPI.getCashRunway(null, { scope: 'group', calendarYear: true }),
      // Stat-card sources: same endpoints their own dashboards use.
      portfolioAPI.getDataset({ year: year }),
      reportsAPI.brosDashboard('ytd'),
      invoiceFinancingAPI.getData({}),
      assetsLiabilitiesAPI.getBalanceSheet('entity', null, 'GBP'),
      assetsLiabilitiesAPI.getBalanceSheet('personal', null, 'GBP'),
      // EUR balance sheets too: the endpoint converts from each item's native
      // currency itself, so EUR display never round-trips through the GBP figure.
      assetsLiabilitiesAPI.getBalanceSheet('entity', null, 'EUR'),
      assetsLiabilitiesAPI.getBalanceSheet('personal', null, 'EUR'),
      bankAccountsAPI.getSummary({}),
      // Gross Revenue chart: Portfolio forward-booking floor (forecast months).
      forecastAPI.get({ scope: 'portfolio', region: 'all' }),
      // Bank Balance chart: actual jurisdiction history + forecast continuation.
      reportsAPI.getCashDevelopment(12),
      // Net Assets chart (dashboards round 3): calendar-year Jan-Dec series,
      // business + personal (summed by month, same convention the Net Assets
      // card's headline uses). Fetched in BOTH currencies -- the endpoint
      // converts each component from its own native currency, same as the
      // balance-sheet calls above; a flat GBP/spot-rate division here would
      // silently diverge from the EUR card (the round-trip-through-GBP bug
      // class this codebase treats as critical).
      assetsLiabilitiesAPI.getNetAssetsSeries('entity', null, 'GBP', 24, true),
      assetsLiabilitiesAPI.getNetAssetsSeries('personal', null, 'GBP', 24, true),
      assetsLiabilitiesAPI.getNetAssetsSeries('entity', null, 'EUR', 24, true),
      assetsLiabilitiesAPI.getNetAssetsSeries('personal', null, 'EUR', 24, true),
      // BR OS P&L (the BR OS scope's own P&L page): its net is the Overview's
      // BR OS net margin, so the two can never differ (Fan, 2026-10-25).
      reportsAPI.getEntityPnl({ entity_id: 'br_ltd', year: year }),
    ]).then(function (results) {
      var res = results[0];
      var runwayRes = results[1];
      var portfolioRes = results[2];
      var brosRes = results[3];
      var financingRes = results[4];
      var businessBsRes = results[5];
      var personalBsRes = results[6];
      var businessBsEurRes = results[7];
      var personalBsEurRes = results[8];
      var bankSummaryRes = results[9];
      var forecastRes = results[10];
      var cashDevRes = results[11];
      var businessNetAssetsSeriesRes = results[12];
      var personalNetAssetsSeriesRes = results[13];
      var businessNetAssetsSeriesEurRes = results[14];
      var personalNetAssetsSeriesEurRes = results[15];
      var brosPnlRes = results[16];

      if (res.status === 'fulfilled') setData(res.value.data);
      if (runwayRes.status === 'fulfilled') setCashRunway(runwayRes.value.data);

      // portfolioAPI.getDataset() already unwraps to res.data (unlike the
      // other calls here, which resolve to the full axios response).
      var derived = portfolioRes.status === 'fulfilled'
        ? computeDerivedMetrics(portfolioRes.value)
        : null;
      // derived.totals.combined (not the top-level ytdCombined.combined,
      // which lacks net_revenue_gbp/properties_count) is what
      // PropertyPortfolioPage itself renders as its Gross/Net Revenue and
      // Properties KPI cards; the dataset endpoint is already actuals-cutoff
      // bounded server-side, so this sum is YTD by construction.
      // EUR version re-derives from each property's native currency
      // (convertDerivedToDisplayCurrency), not a GBP-total / rate division.
      // Reused for both the stat-card totals and the Gross Revenue chart's
      // monthly-by-region series, so the two never disagree on the PP figure.
      var derivedEur = derived ? convertDerivedToDisplayCurrency(derived, 'EUR') : null;
      // One basis with the Portfolio pages (Fan, 2026-10-25): the whole
      // current month counts, no actuals-to-today split.
      var ppGbp = derived ? wholeMonthBasis(derived.totals.combined) : {};
      var ppEur = derivedEur ? wholeMonthBasis(derivedEur.totals.combined) : {};
      var brosKpis = brosRes.status === 'fulfilled' ? brosRes.value.data.kpis : {};
      var financingSummary = financingRes.status === 'fulfilled'
        ? financingRes.value.data.summary
        : {};
      var businessNetAssetsGbp = businessBsRes.status === 'fulfilled'
        ? (businessBsRes.value.data.totals || {}).net_assets || 0
        : 0;
      var personalNetAssetsGbp = personalBsRes.status === 'fulfilled'
        ? (personalBsRes.value.data.totals || {}).net_assets || 0
        : 0;
      var businessNetAssetsEur = businessBsEurRes.status === 'fulfilled'
        ? (businessBsEurRes.value.data.totals || {}).net_assets || 0
        : 0;
      var personalNetAssetsEur = personalBsEurRes.status === 'fulfilled'
        ? (personalBsEurRes.value.data.totals || {}).net_assets || 0
        : 0;
      // Fixed/Current split (Net Assets card sublines): same predicate,
      // same pure function (lib/entityStatCards.computeNetAssetsCard), the
      // entity dashboard's Net Assets card already uses -- summed over
      // business + personal, never re-derived.
      var businessNetAssetsCardGbp = businessBsRes.status === 'fulfilled'
        ? computeNetAssetsCard(businessBsRes.value.data) : { fixed: 0, current: 0 };
      var personalNetAssetsCardGbp = personalBsRes.status === 'fulfilled'
        ? computeNetAssetsCard(personalBsRes.value.data) : { fixed: 0, current: 0 };
      var businessNetAssetsCardEur = businessBsEurRes.status === 'fulfilled'
        ? computeNetAssetsCard(businessBsEurRes.value.data) : { fixed: 0, current: 0 };
      var personalNetAssetsCardEur = personalBsEurRes.status === 'fulfilled'
        ? computeNetAssetsCard(personalBsEurRes.value.data) : { fixed: 0, current: 0 };
      var bankSummary = bankSummaryRes.status === 'fulfilled' ? bankSummaryRes.value.data : {};
      // A real zero and a failed balance-sheet fetch must never render the
      // same confident "£0" Net Assets headline: flag it whenever either
      // side of the sum (business or personal) failed, per currency.
      var netAssetsErrorGbp = businessBsRes.status !== 'fulfilled' || personalBsRes.status !== 'fulfilled';
      var netAssetsErrorEur = businessBsEurRes.status !== 'fulfilled' || personalBsEurRes.status !== 'fulfilled';

      setExtras({
        ppGbp: ppGbp,
        ppEur: ppEur,
        ppMonthlyByRegion: derived
          ? { uk: wholeMonthBasis(derived.monthlyByRegion.uk || []), fr: wholeMonthBasis(derived.monthlyByRegion.fr || []) }
          : { uk: [], fr: [] },
        ppMonthlyByRegionEur: derivedEur
          ? (function (n) { return { uk: wholeMonthBasis(n.uk || []), fr: wholeMonthBasis(n.fr || []) }; })(
            normalizeMonthlyByRegionToGbpKeys(derivedEur.monthlyByRegion))
          : { uk: [], fr: [] },
        brosTurnoverGbp: (brosKpis || {}).total_turnover || 0,
        // Net revenue = turnover - supplier cost; gross margin also after
        // lease voids (A148).
        brosNetRevenueGbp: (brosKpis || {}).total_gross_margin || 0,
        brosLeaseVoidsGbp: brosPnlRes && brosPnlRes.status === 'fulfilled'
          ? brosLeaseVoidsYtd(brosPnlRes.value.data, year) : 0,
        // BR OS dashboard months, with each month's lease voids and overheads
        // from the BR OS P&L merged in so the charts use the A148 tiers.
        brosMonths: brosRes.status === 'fulfilled'
          ? mergeBrosPnlCosts(brosRes.value.data.months || [],
            brosPnlRes && brosPnlRes.status === 'fulfilled' ? brosPnlRes.value.data : null)
          : [],
        // 3-month forward BR OS revenue from contracted nights (backend
        // services/bros_forward_revenue.py) -- the BR OS half of every
        // forecast overlay on this dashboard.
        brosForecastMonths: brosRes.status === 'fulfilled' ? (brosRes.value.data.forecast_months || []) : [],
        // Everything between BR OS gross margin (after lease voids) and the
        // BR OS P&L net: invoice financing + overheads (payroll,
        // subscriptions). Falls back to financing alone if the P&L call failed.
        financingCostGbp: brosPnlRes && brosPnlRes.status === 'fulfilled'
          ? ((brosKpis || {}).total_gross_margin || 0) - brosLeaseVoidsYtd(brosPnlRes.value.data, year)
            - brosNetActualYtd(brosPnlRes.value.data, year)
          : parseFloat((financingSummary || {}).total_financing_cost_ytd || 0),
        financingMonthly: financingRes.status === 'fulfilled'
          ? (financingRes.value.data.monthly_series || [])
          : [],
        netAssetsGbp: businessNetAssetsGbp + personalNetAssetsGbp,
        netAssetsEur: businessNetAssetsEur + personalNetAssetsEur,
        netAssetsErrorGbp: netAssetsErrorGbp,
        netAssetsErrorEur: netAssetsErrorEur,
        netAssetsFixedGbp: businessNetAssetsCardGbp.fixed + personalNetAssetsCardGbp.fixed,
        netAssetsCurrentGbp: businessNetAssetsCardGbp.current + personalNetAssetsCardGbp.current,
        netAssetsFixedEur: businessNetAssetsCardEur.fixed + personalNetAssetsCardEur.fixed,
        netAssetsCurrentEur: businessNetAssetsCardEur.current + personalNetAssetsCardEur.current,
        ppPropertiesUk: derived ? derived.totals.uk.properties_count : 0,
        ppPropertiesFr: derived ? derived.totals.fr.properties_count : 0,
        businessNetAssetsSeries: businessNetAssetsSeriesRes.status === 'fulfilled'
          ? (businessNetAssetsSeriesRes.value.data.series || []) : [],
        personalNetAssetsSeries: personalNetAssetsSeriesRes.status === 'fulfilled'
          ? (personalNetAssetsSeriesRes.value.data.series || []) : [],
        businessNetAssetsSeriesEur: businessNetAssetsSeriesEurRes.status === 'fulfilled'
          ? (businessNetAssetsSeriesEurRes.value.data.series || []) : [],
        personalNetAssetsSeriesEur: personalNetAssetsSeriesEurRes.status === 'fulfilled'
          ? (personalNetAssetsSeriesEurRes.value.data.series || []) : [],
        // A null balance ("feed never reported") stays null -- it must NOT
        // become 0, or a downstream sum would silently count it as real money.
        bankAccounts: (bankSummary.accounts || []).map(function (a) {
          return Object.assign({}, a, { balance: a.balance == null ? null : Number(a.balance) });
        }),
        fxRatesSpot: bankSummary.fx_rates_spot || {},
        ppForecastMonthly: forecastRes.status === 'fulfilled' ? (forecastRes.value.data.monthly || []) : [],
        cashDevelopmentMonths: cashDevRes.status === 'fulfilled' ? (cashDevRes.value.data.months || []) : [],
        cashDevelopmentForecastMonths: cashDevRes.status === 'fulfilled'
          ? (cashDevRes.value.data.forecast_months || [])
          : [],
      });

      if (showRefreshToast) toast.success('Dashboard refreshed');
    }).catch(function (err) {
      toast.error('Failed to load group dashboard');
      console.error(err);
    }).finally(function () {
      setLoading(false);
      setRefreshing(false);
    });
  }, []);

  useEffect(function () { fetchData(); }, [fetchData]);

  useEffect(function () {
    var cancelled = false;
    bankBalanceHistoryAPI.get(null, { months: 12, displayCurrency: displayCurrency, scope: 'group' })
      .then(function (res) { if (!cancelled) setGroupBankHistory(res.data); })
      .catch(function () { if (!cancelled) setGroupBankHistory(null); });
    return function () { cancelled = true; };
  }, [displayCurrency]);

  // The "Refresh" button re-fetches the group dashboard's precomputed
  // snapshot (services/dashboard_snapshot.py) via the same out-of-band
  // JobStore pattern every other Diagnostics card uses (CLAUDE.md prompt
  // 15 FIX 1b): POST kicks off a recompute in the background, this polls
  // for completion, THEN re-fetches every panel's data so the numbers on
  // screen actually reflect the fresh snapshot rather than the same
  // still-cached one a bare re-fetch would return.
  var snapshotPolling = useJobPolling({
    fetchStatus: groupDashboardAPI.refreshStatus,
    onCompleted: function () { fetchData(true); },
    onFailed: function (err) {
      setRefreshing(false);
      toast.error('Dashboard refresh failed: ' + err);
    },
  });

  var handleRefresh = function () {
    setRefreshing(true);
    groupDashboardAPI.refresh().then(function (res) {
      snapshotPolling.start(res.data.job_id);
    }).catch(function (err) {
      setRefreshing(false);
      toast.error(getErrorMessage(err));
    });
  };

  var handleExportPDF = function () {
    if (!cards || !data) return;
    setIsExportingPDF(true);
    exportOverallPDF({
      cards: cards,
      grossRevenueRows: grossRevenueChartData,
      cashDevChartData: cashDevelopmentChartData,
      displayCurrency: displayCurrency,
      year: data.year,
    }).then(function () {
      toast.success('PDF exported');
    }).catch(function (err) {
      console.error('Overall dashboard PDF export failed:', err);
      toast.error(getErrorMessage(err));
    }).finally(function () {
      setIsExportingPDF(false);
    });
  };

  var handleExportExcel = function () {
    if (!cards || !data) return;
    setIsExportingExcel(true);
    exportOverallExcel({
      cards: cards,
      grossRevenueRows: grossRevenueChartData,
      cashDevChartData: cashDevelopmentChartData,
      displayCurrency: displayCurrency,
      year: data.year,
    }).then(function () {
      toast.success('Excel exported');
    }).catch(function (err) {
      console.error('Overall dashboard Excel export failed:', err);
      toast.error(getErrorMessage(err));
    }).finally(function () {
      setIsExportingExcel(false);
    });
  };

  var eurGbpRate = useMemo(function () {
    if (!data || !data.fx_rates_used) return 0.855;
    return parseFloat(data.fx_rates_used.EUR_GBP) || 0.855;
  }, [data]);

  var cards = useMemo(function () {
    if (!extras || !data) return null;
    return computeGroupStatCards({
      // Pre-converted to displayCurrency by the fetch (native conversion, no
      // GBP round-trip) -- computeGroupStatCards applies no further math to these.
      pp: displayCurrency === 'GBP' ? extras.ppGbp : extras.ppEur,
      netAssets: displayCurrency === 'GBP' ? extras.netAssetsGbp : extras.netAssetsEur,
      netAssetsFixed: displayCurrency === 'GBP' ? extras.netAssetsFixedGbp : extras.netAssetsFixedEur,
      netAssetsCurrent: displayCurrency === 'GBP' ? extras.netAssetsCurrentGbp : extras.netAssetsCurrentEur,
      netAssetsError: displayCurrency === 'GBP' ? extras.netAssetsErrorGbp : extras.netAssetsErrorEur,
      propertiesUk: extras.ppPropertiesUk,
      propertiesFr: extras.ppPropertiesFr,
      brosTurnoverGbp: extras.brosTurnoverGbp,
      brosNetRevenueGbp: extras.brosNetRevenueGbp,
      brosLeaseVoidsGbp: extras.brosLeaseVoidsGbp,
      financingCostGbp: extras.financingCostGbp,
      cashNow: data.kpis ? data.kpis.cash_now : null,
      // Avg Monthly Net Cashflow card (dashboards round 3): mean of the
      // Monthly Cashflow chart's own calendar-year bars, COMPLETE months
      // only (the current, possibly partial, month is excluded from the
      // average though it still renders as a bar -- see
      // computeAvgNetCashflow's own comment) -- never cashRunway.avg_monthly_net
      // directly (that stays the Cash Runway card's own, unrelated,
      // trailing-6-month figure; it's only passed here as the zero-complete-
      // months fallback, e.g. every January). Computed in GBP (before
      // displayCurrency conversion) so cv() below applies a single
      // conversion, equivalent to averaging the already-converted chart bars
      // since eurGbpRate is one flat spot rate across every month.
      avgMonthlyNetGbp: computeAvgNetCashflow(
        ((cashRunway || {}).calendar_year_months || []).map(function (m) {
          return { netCashflow: m.net == null ? null : parseFloat(m.net) || 0 };
        }),
        parseFloat((cashRunway || {}).avg_monthly_net || 0),
      ),
      avgMonthlyNetByJurisdictionGbp: cashRunway ? cashRunway.avg_monthly_net_by_jurisdiction : {},
      bankAccounts: extras.bankAccounts,
      fxRatesSpot: extras.fxRatesSpot,
      displayCurrency: displayCurrency,
      eurGbpRate: eurGbpRate,
    });
  }, [extras, data, cashRunway, displayCurrency, eurGbpRate]);

  // Portfolio uses its own proper native-currency conversion (ppMonthlyByRegionEur,
  // via convertDerivedToDisplayCurrency -- same fix as the stat cards). BR OS and
  // the forward-booking forecast have no native EUR breakdown, so those arrays are
  // pre-converted via the shared spot rate BEFORE combining, so the pure builder
  // never mixes currencies within a single row.
  var grossRevenueChartData = useMemo(function () {
    if (!extras || !data) return [];
    var conv = function (v) { return displayCurrency === 'GBP' ? v : v / eurGbpRate; };
    var brosMonths = displayCurrency === 'GBP'
      ? extras.brosMonths
      : (extras.brosMonths || []).map(function (m) {
        return Object.assign({}, m, {
          turnover: conv(parseFloat(m.turnover || 0)),
          gross_margin: conv(parseFloat(m.gross_margin || 0)),
          lease_voids: conv(parseFloat(m.lease_voids || 0)),
          overheads: conv(parseFloat(m.overheads || 0)),
        });
      });
    var financingMonthly = displayCurrency === 'GBP'
      ? extras.financingMonthly
      : (extras.financingMonthly || []).map(function (m) {
        return Object.assign({}, m, { total: conv(parseFloat(m.total || 0)) });
      });
    var ppForecastMonthly = displayCurrency === 'GBP'
      ? extras.ppForecastMonthly
      : (extras.ppForecastMonthly || []).map(function (m) {
        return Object.assign({}, m, { gross_revenue_gbp: conv(m.gross_revenue_gbp || 0) });
      });
    var brosForecastMonths = displayCurrency === 'GBP'
      ? extras.brosForecastMonths
      : (extras.brosForecastMonths || []).map(function (m) {
        return Object.assign({}, m, {
          turnover: conv(parseFloat(m.turnover || 0)),
          gross_margin: conv(parseFloat(m.gross_margin || 0)),
        });
      });
    return buildGrossRevenueChartData({
      year: data.year,
      ppMonthlyByRegion: displayCurrency === 'GBP' ? extras.ppMonthlyByRegion : extras.ppMonthlyByRegionEur,
      brosMonths: brosMonths,
      brosForecastMonths: brosForecastMonths,
      financingMonthly: financingMonthly,
      ppForecastMonthly: ppForecastMonthly,
      months: SHORT_MONTHS,
    });
  }, [extras, data, displayCurrency, eurGbpRate]);

  // Margin chart data (companion to grossRevenueChartData above, now that
  // the Gross Revenue chart no longer carries margin lines): same inputs,
  // same currency-conversion convention, just the margin forecast field
  // (margin_gbp) instead of gross_revenue_gbp.
  var groupMarginChartData = useMemo(function () {
    if (!extras || !data) return [];
    var conv = function (v) { return displayCurrency === 'GBP' ? v : v / eurGbpRate; };
    var brosMonths = displayCurrency === 'GBP'
      ? extras.brosMonths
      : (extras.brosMonths || []).map(function (m) {
        return Object.assign({}, m, {
          turnover: conv(parseFloat(m.turnover || 0)),
          gross_margin: conv(parseFloat(m.gross_margin || 0)),
          lease_voids: conv(parseFloat(m.lease_voids || 0)),
          overheads: conv(parseFloat(m.overheads || 0)),
        });
      });
    var financingMonthly = displayCurrency === 'GBP'
      ? extras.financingMonthly
      : (extras.financingMonthly || []).map(function (m) {
        return Object.assign({}, m, { total: conv(parseFloat(m.total || 0)) });
      });
    var ppForecastMonthly = displayCurrency === 'GBP'
      ? extras.ppForecastMonthly
      : (extras.ppForecastMonthly || []).map(function (m) {
        return Object.assign({}, m, { margin_gbp: conv(m.margin_gbp || 0) });
      });
    var brosForecastMonths = displayCurrency === 'GBP'
      ? extras.brosForecastMonths
      : (extras.brosForecastMonths || []).map(function (m) {
        return Object.assign({}, m, {
          turnover: conv(parseFloat(m.turnover || 0)),
          gross_margin: conv(parseFloat(m.gross_margin || 0)),
        });
      });
    return buildGroupMarginChartData({
      year: data.year,
      ppMonthlyByRegion: displayCurrency === 'GBP' ? extras.ppMonthlyByRegion : extras.ppMonthlyByRegionEur,
      brosMonths: brosMonths,
      brosForecastMonths: brosForecastMonths,
      financingMonthly: financingMonthly,
      ppForecastMonthly: ppForecastMonthly,
      months: SHORT_MONTHS,
    });
  }, [extras, data, displayCurrency, eurGbpRate]);

  // Net Revenue chart data (Fan, 2026-08-30). Portfolio = booking volume
  // net of OTA commission (portfolioDerived's own `revenue`); BR OS =
  // turnover net of the supplier cost (its gross margin). Same conversion
  // convention as the two charts above, so all three agree per month.
  var netRevenueChartData = useMemo(function () {
    if (!extras || !data) return [];
    var conv = function (v) { return displayCurrency === 'GBP' ? v : v / eurGbpRate; };
    var brosMonths = displayCurrency === 'GBP'
      ? extras.brosMonths
      : (extras.brosMonths || []).map(function (m) {
        return Object.assign({}, m, { gross_margin: conv(parseFloat(m.gross_margin || 0)) });
      });
    var brosForecastMonths = displayCurrency === 'GBP'
      ? extras.brosForecastMonths
      : (extras.brosForecastMonths || []).map(function (m) {
        return Object.assign({}, m, { gross_margin: conv(parseFloat(m.gross_margin || 0)) });
      });
    var ppForecastMonthly = displayCurrency === 'GBP'
      ? extras.ppForecastMonthly
      : (extras.ppForecastMonthly || []).map(function (m) {
        return Object.assign({}, m, { revenue_gbp: conv(m.revenue_gbp || 0) });
      });
    return buildNetRevenueChartData({
      year: data.year,
      ppMonthlyByRegion: displayCurrency === 'GBP' ? extras.ppMonthlyByRegion : extras.ppMonthlyByRegionEur,
      brosMonths: brosMonths,
      brosForecastMonths: brosForecastMonths,
      ppForecastMonthly: ppForecastMonthly,
      months: SHORT_MONTHS,
    });
  }, [extras, data, displayCurrency, eurGbpRate]);

  // Monthly Cashflow chart data (dashboards round 3): bars are
  // cashRunway.calendar_year_months verbatim (Jan-Dec, requested via
  // calendarYear: true above), converted from GBP (the same convention
  // avgMonthlyNetGbp above uses) -- the exact figures the Avg Monthly Net
  // Cashflow card averages (over populated months only), so the card equals
  // the mean of the bars by construction. Net margin line from the Gross
  // Revenue chart's own rows, already in displayCurrency.
  var monthlyCashflowChartData = useMemo(function () {
    var conv = function (v) { return displayCurrency === 'GBP' ? v : v / eurGbpRate; };
    var calendarYearMonths = ((cashRunway || {}).calendar_year_months || []).map(function (m) {
      return { month: m.month, net: m.net == null ? null : conv(parseFloat(m.net) || 0) };
    });
    return buildMonthlyCashflowChartData({
      calendarYearMonths: calendarYearMonths,
      netMarginRows: grossRevenueChartData,
    });
  }, [cashRunway, grossRevenueChartData, displayCurrency, eurGbpRate]);

  var cashDevelopmentChartData = useMemo(function () {
    if (!extras) return { actual: [] };
    return buildCashDevelopmentChartData({
      months: extras.cashDevelopmentMonths,
      displayCurrency: displayCurrency,
      eurGbpRate: eurGbpRate,
    });
  }, [extras, displayCurrency, eurGbpRate]);

  // Bank Balance chart data (per-bank lines + black consolidation line):
  // built from the SAME bank-balance-history series/component the entity and
  // aggregate dashboards use (scope='group' here, every real account
  // including unassigned) -- see lib/entityBankBalanceHistoryChart.js.
  // cashDevelopmentChartData above is kept only for the PDF/Excel export's
  // existing per-jurisdiction table (a presentation regrouping, not a data
  // migration -- CLAUDE.md), it no longer feeds an on-screen chart.
  var bankBalanceHistoryChartData = useMemo(function () {
    if (!groupBankHistory) return { rows: [], series: [] };
    return buildBankBalanceHistoryChartData({
      monthsData: groupBankHistory.months,
      year: new Date().getFullYear(),
    });
  }, [groupBankHistory]);

  // Net Assets 12m chart data (part 1b): business + personal series summed by
  // month, converted from the GBP-only source (same convention avgMonthlyNetGbp
  // above uses). Forecast continuation reads the raw group cash forecast
  // (extras.cashDevelopmentForecastMonths, still returned by GET
  // /reports/group/cash-development) directly via
  // buildJurisdictionCashForecastRows -- NOT through cashDevelopmentChartData,
  // which dropped its own forecast segment in dashboards round 3.
  var netAssetsChartData = useMemo(function () {
    if (!extras) return [];
    // Pick the currency-matching series pair (each fetched natively in that
    // currency, same as businessBsRes/businessBsEurRes above) rather than a
    // flat GBP/spot-rate division -- that would silently diverge from the
    // EUR Net Assets card, the round-trip-through-GBP bug class this
    // codebase treats as critical.
    var series = displayCurrency === 'GBP'
      ? sumNetAssetsSeries([extras.businessNetAssetsSeries, extras.personalNetAssetsSeries])
      : sumNetAssetsSeries([extras.businessNetAssetsSeriesEur, extras.personalNetAssetsSeriesEur]);
    var cashForecastRows = buildJurisdictionCashForecastRows(
      extras.cashDevelopmentForecastMonths, displayCurrency, eurGbpRate,
    );
    return buildNetAssetsChartData({
      series: series,
      currentCash: cards ? cards.bankBalance.headline : 0,
      cashForecastRows: cashForecastRows,
    });
  }, [extras, cards, displayCurrency, eurGbpRate]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <AlertTriangle className="h-8 w-8 text-muted-foreground" />
        <p className="text-muted-foreground">Failed to load dashboard data.</p>
        <Button variant="outline" onClick={handleRefresh}>Retry</Button>
      </div>
    );
  }

  var kpis = data.kpis;
  var fx_exposure = data.fx_exposure;

  return (
    <div className="flex flex-col gap-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Group Dashboard</h1>
          <p className="text-muted-foreground mt-1">
            Multi-entity consolidated view, {data.year}
            {data.computed_at && (
              <span
                className={'ml-2' + ((Date.now() - new Date(data.computed_at).getTime()) > 2 * 3600 * 1000 ? ' text-amber-600 font-medium' : '')}
                data-testid="group-dashboard-as-of"
                title="The figures on this page are refreshed every 15 minutes. An old date means the refresh is failing."
              >
                &middot; as of {new Date(data.computed_at).toLocaleString([], { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <CurrencyToggle />
          <Button
            variant="outline"
            size="sm"
            onClick={handleExportPDF}
            disabled={isExportingPDF || !cards}
            data-testid="export-pdf-btn"
          >
            {isExportingPDF
              ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              : <Download className="h-4 w-4 mr-2" />}
            Export PDF
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleExportExcel}
            disabled={isExportingExcel || !cards}
            data-testid="export-excel-btn"
          >
            {isExportingExcel
              ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              : <Download className="h-4 w-4 mr-2" />}
            Export Excel
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={refreshing}
          >
            <RefreshCw className={'h-4 w-4 mr-2 ' + (refreshing ? 'animate-spin' : '')} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Stat Cards */}
      {cards && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard
              label="Booking Volume"
              value={formatCompact(cards.grossRevenue.headline, displayCurrency)}
              valueTitle={formatCurrencyFull(cards.grossRevenue.headline, displayCurrency)}
              icon={TrendingUp}
              showFreshness={false}
              lines={[
                { label: 'Property Portfolio', value: formatCompact(cards.grossRevenue.pp, displayCurrency), title: formatCurrencyFull(cards.grossRevenue.pp, displayCurrency) },
                { label: 'BR OS', value: formatCompact(cards.grossRevenue.brosOs, displayCurrency), title: formatCurrencyFull(cards.grossRevenue.brosOs, displayCurrency) },
              ]}
            />
            <StatCard
              label="Net Revenue"
              value={formatCompact(cards.netRevenue.headline, displayCurrency)}
              valueTitle={formatCurrencyFull(cards.netRevenue.headline, displayCurrency)}
              icon={TrendingUp}
              showFreshness={false}
              lines={[
                { label: 'Property Portfolio', value: formatCompact(cards.netRevenue.pp, displayCurrency), title: formatCurrencyFull(cards.netRevenue.pp, displayCurrency) },
                { label: 'BR OS', value: formatCompact(cards.netRevenue.brosOs, displayCurrency), title: formatCurrencyFull(cards.netRevenue.brosOs, displayCurrency) },
              ]}
            />
            <StatCard
              label="Gross Margin"
              value={formatCompact(cards.grossMargin.headline, displayCurrency)}
              valueTitle={formatCurrencyFull(cards.grossMargin.headline, displayCurrency)}
              percentage={formatPercent(cards.grossMargin.pct, 1)}
              icon={PieChart}
              showFreshness={false}
              lines={[
                { label: 'Property Portfolio', value: formatCompact(cards.grossMargin.pp, displayCurrency), title: formatCurrencyFull(cards.grossMargin.pp, displayCurrency) },
                { label: 'BR OS', value: formatCompact(cards.grossMargin.brosOs, displayCurrency), title: formatCurrencyFull(cards.grossMargin.brosOs, displayCurrency) },
              ]}
            />
            <StatCard
              label="Net Margin"
              value={formatCompact(cards.netMargin.headline, displayCurrency)}
              valueTitle={formatCurrencyFull(cards.netMargin.headline, displayCurrency)}
              percentage={formatPercent(cards.netMargin.pct, 1)}
              icon={PieChart}
              showFreshness={false}
              className={cards.netMargin.headline < 0 ? 'border-red-200 dark:border-red-800' : ''}
              lines={[
                { label: 'Property Portfolio', value: formatCompact(cards.netMargin.pp, displayCurrency), title: formatCurrencyFull(cards.netMargin.pp, displayCurrency) },
                { label: 'BR OS', value: formatCompact(cards.netMargin.brosOs, displayCurrency), title: formatCurrencyFull(cards.netMargin.brosOs, displayCurrency) },
              ]}
            />
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard
              label="Bank Balance"
              value={formatCompact(cards.bankBalance.headline, displayCurrency)}
              valueTitle={formatCurrencyFull(cards.bankBalance.headline, displayCurrency)}
              icon={Wallet}
              freshness={kpis.cash_now ? kpis.cash_now.freshness : null}
              stale={kpis.cash_now ? kpis.cash_now.stale : null}
              staleConnections={kpis.cash_now ? kpis.cash_now.stale_connections : null}
              lines={[
                { label: '🇬🇧 UK', value: formatCompact(cards.bankBalance.uk, displayCurrency), title: formatCurrencyFull(cards.bankBalance.uk, displayCurrency) },
                { label: '🇫🇷 France', value: formatCompact(cards.bankBalance.france, displayCurrency), title: formatCurrencyFull(cards.bankBalance.france, displayCurrency) },
                { label: 'Personal', value: formatCompact(cards.bankBalance.personal, displayCurrency), title: formatCurrencyFull(cards.bankBalance.personal, displayCurrency) },
              ].concat(cards.bankBalance.unassigned > 0.005
                ? [{ label: 'Unassigned', value: formatCompact(cards.bankBalance.unassigned, displayCurrency), title: formatCurrencyFull(cards.bankBalance.unassigned, displayCurrency) }]
                : [])}
            />
            <StatCard
              label="Net Assets"
              value={cards.netAssetsCard.error ? 'No data' : formatCompact(cards.netAssetsCard.headline, displayCurrency)}
              valueTitle={cards.netAssetsCard.error ? undefined : formatCurrencyFull(cards.netAssetsCard.headline, displayCurrency)}
              icon={Building2}
              showFreshness={false}
              lines={cards.netAssetsCard.error ? [] : [
                { label: 'Fixed', value: formatCompact(cards.netAssetsCard.fixed, displayCurrency), title: formatCurrencyFull(cards.netAssetsCard.fixed, displayCurrency) },
                { label: 'Current', value: formatCompact(cards.netAssetsCard.current, displayCurrency), title: formatCurrencyFull(cards.netAssetsCard.current, displayCurrency) },
              ]}
            />
            <StatCard
              label="Properties"
              value={String(cards.propertiesCard.headline)}
              icon={Home}
              showFreshness={false}
              lines={[
                { label: '🇬🇧 UK', value: String(cards.propertiesCard.uk) },
                { label: '🇫🇷 France', value: String(cards.propertiesCard.france) },
              ]}
            />
            <StatCard
              label="Avg Net Cashflow (YTD, complete months)"
              value={formatCompact(cards.avgMonthlyNetCashflowCard.headline, displayCurrency)}
              valueTitle={formatCurrencyFull(cards.avgMonthlyNetCashflowCard.headline, displayCurrency)}
              icon={Clock}
              showFreshness={false}
              lines={[
                { label: '🇬🇧 UK', value: formatCompact(cards.avgMonthlyNetCashflowCard.uk, displayCurrency), title: formatCurrencyFull(cards.avgMonthlyNetCashflowCard.uk, displayCurrency) },
                { label: '🇫🇷 France', value: formatCompact(cards.avgMonthlyNetCashflowCard.france, displayCurrency), title: formatCurrencyFull(cards.avgMonthlyNetCashflowCard.france, displayCurrency) },
                { label: 'Personal', value: formatCompact(cards.avgMonthlyNetCashflowCard.personal, displayCurrency), title: formatCurrencyFull(cards.avgMonthlyNetCashflowCard.personal, displayCurrency) },
              ]}
            />
          </div>
        </>
      )}

      {/* Gross Revenue (monthly): BR OS + Portfolio stacked bar, PP forecast */}
      <GrossRevenueChart
        chartData={grossRevenueChartData}
        displayCurrency={displayCurrency}
        year={data.year}
      />

      {/* Net Revenue (monthly): booking volume after OTA commission
          (Portfolio) and after supplier cost (BR OS). */}
      <NetRevenueChart
        chartData={netRevenueChartData}
        displayCurrency={displayCurrency}
        year={data.year}
      />

      {/* Margin (monthly): Net Margin stacked BR OS + Portfolio, Gross
          Margin combined line -- directly below Gross Revenue, same
          BR OS/Portfolio segment split as that chart. */}
      <MarginChart
        chartData={groupMarginChartData}
        segments={GROSS_REVENUE_SEGMENTS}
        displayCurrency={displayCurrency}
        year={data.year}
      />

      {/* By Jurisdiction Bank Balances (redesign round 2, part 2 layout):
          moved here, directly below the Gross Revenue chart. */}
      <GroupJurisdictionTab />

      {/* Cash Runway | FX Exposure (dashboards round 3 layout: the ONLY
          place Cash Runway appears on this dashboard). */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <CashRunwayCard
          // avg_monthly_net comes back GBP-native (this card's OWN
          // trailing-6-month figure -- no longer the Avg Monthly Net
          // Cashflow stat card's headline, which moved to a calendar-year
          // mean in dashboards round 3 and is a genuinely different window
          // now, not just a re-conversion of the same number). Convert with
          // the same flat spot rate the rest of this component uses so an
          // EUR-toggled view doesn't show the raw GBP figure mislabeled.
          cashRunway={cashRunway ? {
            ...cashRunway,
            avg_monthly_net: displayCurrency === 'GBP'
              ? cashRunway.avg_monthly_net
              : (parseFloat(cashRunway.avg_monthly_net || 0) / eurGbpRate),
          } : cashRunway}
          currentCash={cards.bankBalance.headline}
          currency={displayCurrency}
          formatCurrency={formatCurrencyFull}
        />
        <FxExposurePanel
          fxExposure={fx_exposure}
          displayCurrency={displayCurrency}
        />
      </div>

      {/* Bank Balance: per-bank lines + black consolidation line, calendar-year, full width. */}
      <BankBalanceHistoryChart
        rows={bankBalanceHistoryChartData.rows}
        series={bankBalanceHistoryChartData.series}
        displayCurrency={displayCurrency}
        unknownBankAccounts={groupBankHistory?.unknown_bank_accounts || []}
      />

      {/* Net Assets: calendar-year + 3-month forecast, full width. */}
      <NetAssetsChart
        chartData={netAssetsChartData}
        displayCurrency={displayCurrency}
      />

      {/* Monthly Cashflow: calendar-year, full width. */}
      <MonthlyCashflowChart
        chartData={monthlyCashflowChartData}
        displayCurrency={displayCurrency}
      />
    </div>
  );
}
